/**
 * WrappedPage  — /wrapped
 *
 * Authenticated view: computes & displays the user's "Relationship Wrapped"
 * for the current year. Each stat card fades + slides up as it scrolls into
 * view (IntersectionObserver). A share button at the bottom gives a public URL.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import DashboardNav from '../components/dashboard/DashboardNav';

// ── Types ─────────────────────────────────────────────────────────────────────

interface WrappedStats {
  year:                       number;
  total_cards:                number;
  total_people:               number;
  total_countries:            number;
  most_carded_contact_name:   string | null;
  most_carded_contact_count:  number;
  top_occasion_type:          string | null;
  months_active:              number;
  streak_years:               number;
  share_token:                string | null;
  share_url:                  string | null;
}

// ── Occasion labels ───────────────────────────────────────────────────────────

const OCCASION_LABEL: Record<string, string> = {
  birthday:       'Birthdays',
  anniversary:    'Anniversaries',
  christmas:      'Christmas',
  valentines:     'Valentine\'s Day',
  mothers_day:    'Mother\'s Day',
  fathers_day:    'Father\'s Day',
  graduation:     'Graduation',
  thinking_of_you:'Thinking of You',
  custom:         'Special Moments',
};

const OCCASION_EMOJI: Record<string, string> = {
  birthday:       '🎂',
  anniversary:    '💍',
  christmas:      '🎄',
  valentines:     '💝',
  mothers_day:    '🌹',
  fathers_day:    '👔',
  graduation:     '🎓',
  thinking_of_you:'💌',
  custom:         '✨',
};

// ── Animated counter hook ─────────────────────────────────────────────────────

function useCountUp(target: number, duration = 1200, active = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active || target === 0) { setValue(target); return; }
    const startTime = performance.now();
    function tick(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }, [target, duration, active]);
  return value;
}

// ── Fade-in on scroll ─────────────────────────────────────────────────────────

function useFadeIn() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return { ref, visible };
}

// ── Stat card component ───────────────────────────────────────────────────────

interface StatCardProps {
  label:    string;
  value:    number | string;
  sub?:     string;
  emoji?:   string;
  accent?:  'gold' | 'sage' | 'rose';
  delay?:   number;
  isNumber?: boolean;
}

function StatCard({ label, value, sub, emoji, accent = 'gold', delay = 0, isNumber = true }: StatCardProps) {
  const { ref, visible } = useFadeIn();
  const numericValue = typeof value === 'number' ? value : 0;
  const counted = useCountUp(numericValue, 1200, visible && isNumber);

  const accentColor = {
    gold: 'text-[#B59518]',
    sage: 'text-[#E8C12E]',
    rose: 'text-[#FAEED8]',
  }[accent];

  return (
    <div
      ref={ref}
      style={{
        transitionDelay: `${delay}ms`,
        opacity:    visible ? 1 : 0,
        transform:  visible ? 'translateY(0)' : 'translateY(28px)',
        transition: 'opacity 0.55s ease, transform 0.55s ease',
      }}
      className="bg-[#1A1410] rounded-[24px] p-8 flex flex-col gap-3 shadow-xl"
    >
      {emoji && <span className="text-4xl">{emoji}</span>}
      <p className="font-body text-sm text-white/50 uppercase tracking-wider">{label}</p>
      <p className={`font-heading text-6xl font-bold ${accentColor} leading-none`}>
        {isNumber ? counted.toLocaleString() : value}
      </p>
      {sub && (
        <p className="font-body text-sm text-white/70 leading-relaxed">{sub}</p>
      )}
    </div>
  );
}

// ── Share strip ───────────────────────────────────────────────────────────────

function ShareStrip({ shareUrl, year }: { shareUrl: string; year: number }) {
  const { ref, visible } = useFadeIn();
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(shareUrl).catch(() => {
      prompt('Copy your Wrapped link:', shareUrl);
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  function share() {
    if (navigator.share) {
      navigator.share({
        title: `My ${year} Relationship Wrapped on MomentSent`,
        text:  `I sent cards to real people all year, check out my ${year} card recap! 💌`,
        url:   shareUrl,
      }).catch(() => {});
    } else {
      copy();
    }
  }

  return (
    <div
      ref={ref}
      style={{
        opacity:    visible ? 1 : 0,
        transform:  visible ? 'translateY(0)' : 'translateY(28px)',
        transition: 'opacity 0.55s ease, transform 0.55s ease',
      }}
      className="bg-[#B59518] rounded-[24px] p-8 text-center shadow-xl"
    >
      <p className="font-heading text-2xl font-bold text-[#1A1410] mb-2">Share your Wrapped</p>
      <p className="font-body text-sm text-[#1A1410]/70 mb-6">
        Show the world you showed up for the people you love.
      </p>
      <div className="flex gap-3 justify-center flex-wrap">
        <button
          onClick={share}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1A1410] text-white
                     font-body text-sm font-semibold hover:bg-[#1A1410]/85 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share
        </button>
        <button
          onClick={copy}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-[#1A1410]/30
                     text-[#1A1410] font-body text-sm font-semibold hover:bg-[#1A1410]/10 transition-colors"
        >
          {copied ? (
            <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg> Copied!</>
          ) : (
            <>Copy link</>
          )}
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function WrappedPage() {
  const [stats,   setStats]   = useState<WrappedStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const year = new Date().getFullYear();

  const load = useCallback(async () => {
    try {
      const json = await apiFetch<WrappedStats>(`/api/wrapped/${year}`);
      setStats(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-cream">
      <DashboardNav />

      {/* Hero banner */}
      <div className="bg-[#1A1410] py-16 px-4 text-center">
        <p className="font-body text-[#B59518] text-sm font-semibold uppercase tracking-widest mb-3">
          Relationship Wrapped
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-3">
          Your {year} in Cards
        </h1>
        <p className="font-body text-white/60 text-sm">
          Every stamp. Every smile. Every moment you chose to show up.
        </p>
      </div>

      <main className="max-w-2xl mx-auto px-4 py-12 pb-24 sm:pb-12">

        {/* Loading */}
        {loading && (
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-44 rounded-[24px] bg-[#1A1410]/10 animate-pulse" />
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="text-center py-20">
            <div className="text-5xl mb-5">😔</div>
            <h2 className="font-heading text-2xl text-navy mb-3">Couldn't load your Wrapped</h2>
            <p className="font-body text-sm text-gray-500 mb-6">
              Something went wrong on our end. Give it another shot.
            </p>
            <button
              onClick={load}
              className="btn-primary px-8 mb-4"
            >
              Try again
            </button>
            <div>
              <Link to="/dashboard" className="font-body text-sm text-gray-400 hover:text-navy transition-colors">
                ← Back to dashboard
              </Link>
            </div>
          </div>
        )}

        {/* No cards this year */}
        {!loading && !error && stats?.total_cards === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-6">💌</div>
            <h2 className="font-heading text-2xl text-navy mb-3">
              No cards sent in {year} yet
            </h2>
            <p className="font-body text-sm text-gray-500 leading-relaxed mb-2 max-w-sm mx-auto">
              Your Wrapped fills up as cards go out the door. Add a contact and
              their first occasion, we'll handle the rest.
            </p>
            <p className="font-body text-xs text-gray-400 mb-8">
              Cards are mailed automatically before each occasion you set up.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/dashboard" className="btn-primary px-8">
                Add a contact
              </Link>
              <Link to="/dashboard" className="btn-secondary px-8">
                Go to dashboard
              </Link>
            </div>
          </div>
        )}

        {/* Stats grid */}
        {!loading && !error && stats && stats.total_cards > 0 && (
          <div className="space-y-4">

            {/* Total cards */}
            <StatCard
              label="Cards sent"
              value={stats.total_cards}
              sub="Physical cards that arrived in someone's mailbox this year."
              emoji="📬"
              accent="gold"
              delay={0}
            />

            {/* Total people */}
            <StatCard
              label="People you showed up for"
              value={stats.total_people}
              sub={stats.total_people === 1
                ? 'One person felt a little more loved this year because of you.'
                : `${stats.total_people} people found a card in their mailbox from you.`}
              emoji="❤️"
              accent="rose"
              delay={80}
            />

            {/* Most-carded person */}
            {stats.most_carded_contact_name && stats.most_carded_contact_count > 1 && (
              <div
                style={{ opacity: 0, transform: 'translateY(28px)' }}
                className="bg-[#1A1410] rounded-[24px] p-8 shadow-xl animate-none"
                ref={(el) => {
                  if (!el) return;
                  const obs = new IntersectionObserver(([entry]) => {
                    if (entry.isIntersecting) {
                      el.style.transition = 'opacity 0.55s ease 160ms, transform 0.55s ease 160ms';
                      el.style.opacity    = '1';
                      el.style.transform  = 'translateY(0)';
                      obs.disconnect();
                    }
                  }, { threshold: 0.15 });
                  obs.observe(el);
                }}
              >
                <span className="text-4xl">🏆</span>
                <p className="font-body text-sm text-white/50 uppercase tracking-wider mt-3">
                  Your most-loved person
                </p>
                <p className="font-heading text-4xl font-bold text-[#B59518] mt-2 leading-tight">
                  {stats.most_carded_contact_name}
                </p>
                <p className="font-body text-sm text-white/70 mt-2">
                  Received {stats.most_carded_contact_count} card{stats.most_carded_contact_count !== 1 ? 's' : ''} from you this year.
                </p>
              </div>
            )}

            {/* Top occasion */}
            {stats.top_occasion_type && (
              <StatCard
                label="Favorite occasion"
                value={OCCASION_EMOJI[stats.top_occasion_type] ?? '🎉'}
                sub={`You sent the most cards for ${OCCASION_LABEL[stats.top_occasion_type] ?? stats.top_occasion_type}.`}
                accent="sage"
                delay={240}
                isNumber={false}
              />
            )}

            {/* Countries */}
            {stats.total_countries > 1 && (
              <StatCard
                label="Countries reached"
                value={stats.total_countries}
                sub="Your kindness crossed borders and arrived across the world."
                emoji="🌍"
                accent="sage"
                delay={320}
              />
            )}

            {/* Months active */}
            <StatCard
              label="Months you sent"
              value={stats.months_active}
              sub={`out of 12, ${
                stats.months_active >= 10 ? 'an incredible, consistent year of caring.' :
                stats.months_active >= 6  ? 'you showed up more often than most.' :
                'every month you sent counts.'
              }`}
              emoji="📅"
              accent="gold"
              delay={400}
            />

            {/* Streak */}
            {stats.streak_years > 1 && (
              <StatCard
                label="Year streak"
                value={stats.streak_years}
                sub={`You've sent cards ${stats.streak_years} years in a row. Don't break it.`}
                emoji="🔥"
                accent="rose"
                delay={480}
              />
            )}

            {/* Closing message */}
            <ClosingMessage stats={stats} year={year} />

            {/* Share */}
            {stats.share_url && (
              <ShareStrip shareUrl={stats.share_url} year={year} />
            )}

            {/* Back */}
            <div className="text-center pt-4">
              <Link to="/dashboard" className="font-body text-sm text-gray-400 hover:text-navy transition-colors">
                ← Back to dashboard
              </Link>
            </div>

          </div>
        )}
      </main>
    </div>
  );
}

// ── Closing message slide ─────────────────────────────────────────────────────

function ClosingMessage({ stats, year }: { stats: WrappedStats; year: number }) {
  const { ref, visible } = useFadeIn();

  const message = stats.total_cards >= 50
    ? `In a world of notifications and DMs, you chose something real. ${stats.total_cards} times.`
    : stats.total_cards >= 20
    ? `Most people mean to send cards. You actually did, ${stats.total_cards} times this year.`
    : stats.total_people === 1
    ? `One person. One card. That's still everything to them.`
    : `Every one of those ${stats.total_cards} cards was a choice to make someone feel remembered.`;

  return (
    <div
      ref={ref}
      style={{
        opacity:    visible ? 1 : 0,
        transform:  visible ? 'translateY(0)' : 'translateY(28px)',
        transition: 'opacity 0.65s ease 100ms, transform 0.65s ease 100ms',
      }}
      className="text-center py-6 px-4"
    >
      <p className="font-heading text-xl text-navy leading-relaxed">{message}</p>
      <p className="font-body text-sm text-gray-400 mt-3">, MomentSent {year}</p>
    </div>
  );
}
